import api from '@/libs/axiosInstance';
import type { AuthTokens } from '@/libs/token-manager';
import type { ApiResponse, RegisterBrandRequest, RegisterBrandResponse } from '@fluenceur/types';
import { useMutation } from '@tanstack/react-query';
import { storeBrandSessionTokens } from './brand-session-tokens';

/**
 * Creates a brand account on its own, without an invitation
 * (`POST /auth/brands/register`), from the code `POST /auth/brands/signup-code`
 * sent to the address: organization, brand and owner in one go, on the free
 * plan. A wrong or spent code answers 400, too many tries 429. Refused with a 409 for an address a creator account holds, an
 * address already registered, or one an admin invitation waits for.
 */
const registerBrandRequest = async (data: RegisterBrandRequest): Promise<AuthTokens> => {
  const { data: response } = await api.post<ApiResponse<RegisterBrandResponse>>(
    '/auth/brands/register',
    data,
    { withCredentials: true }
  );

  if (!response.data) {
    throw new Error(response.message || 'Failed to register the brand');
  }

  return storeBrandSessionTokens(response.data.token);
};

export const useRegisterBrandAction = () =>
  useMutation<AuthTokens, Error, RegisterBrandRequest>({
    mutationFn: registerBrandRequest,
  });
